'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import {parse} from 'qs';
import {sprintf} from 'sprintf-js';

import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import termStore from "@uw-it-sis/lib-react/lib/TermStore";
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import DocumentTitle from '@uw-it-sis/lib-react/lib/DocumentTitle';
import Loading from '@uw-it-sis/lib-react/lib/Loading';
import HelpPopover from '@uw-it-sis/lib-react/lib/HelpPopover';
import {isEmpty} from '@uw-it-sis/lib-react/lib/Utils';
import Broken from "@uw-it-sis/lib-react/lib/Broken";
import UnknownState from "@uw-it-sis/lib-react/lib/UnknownState";
import {withRouter} from "@uw-it-sis/lib-react/lib/WithRouter";

import BookmarkWidget from '@uw-it-sis/lib-react-myplan/lib/BookmarkWidget';
import OrgUtils from '@uw-it-sis/lib-react-myplan/lib/OrgUtils';
import AddCourseToPlanForm from '@uw-it-sis/lib-react-myplan/lib/AddCourseToPlanForm';
import LinkBuilder from '@uw-it-sis/lib-react-myplan/lib/LinkBuilder';
import {normalize} from '@uw-it-sis/lib-react-myplan/lib/CourseUtils';
import CourseMessages from '@uw-it-sis/lib-react-myplan/lib/CourseMessages';
import CourseDetailsStore from '@uw-it-sis/lib-react-myplan/lib/CourseDetailsStore';
import GenEdUtils from '@uw-it-sis/lib-react-myplan/lib/GenEdUtils';

import SectionsPanel from './SectionsPanel';
import CourseVersions from './CourseVersions';

const GenEdList = GenEdUtils.getList().map((genEd, i) => {
    return <li key={i}><strong>{genEd.code}</strong> - {genEd.title}</li>;
});

const curricularDisclaimer = <p><strong>Disclaimer:</strong> This information is provided for informational purposes only and is subject to change. Students are responsible for reviewing their degree audit and consulting with the appropriate academic unit and/or their adviser for more current or specific information.</p>

const CourseDetailsHelp = {
    "prereq":
        <>
            <p><strong>Prerequisite</strong> - a course that must be completed satisfactorily before you can take the course in question.</p>
            <p><strong>Co-requisite</strong> - a course that must be taken at the same time as the course in question. Co-requisites are sometimes listed in the Time Schedule as "concurrent registration required in..."</p>
        </>,
    "quarter":
        <>
            <p>Projected courses indicate which quarter the course might be offered next. Contact the department for verification. Scheduled courses appear in the current Time Schedule.</p>
        </>,
    "gened":
        <>
            <ul className="list-unstyled space-y-2">
                {GenEdList}
                <li><strong>None</strong> - Does not count towards General Education Requirements</li>
            </ul>
            <p><a target="_blank" href="https://www.washington.edu/uaa/advising/degree-overview/general-education/">Learn More on the UAA Advising Website</a></p>
        </>,
    "crossListing":
        <>
            <p>A jointly offered course is a course offered by two or more departments.</p>
            <p><a target="_blank" href="http://depts.washington.edu/registra/curriculum/jointCourses.php">Learn More</a></p>
        </>,
    "recommendedPrep":
        <>
            <p>Course(s) or subject areas recommended to students prior to taking this course.</p>
        </>,
    "equivalentCourses":
        <>
            <p><strong>Equivalent</strong> - Course is considered the same as another course (or courses). Equivalent courses substitute for each other as degree requirements and prerequisites. Credit is only given to one of the courses if a student completes more than one.</p>
            {curricularDisclaimer}
        </>,
    "overlapping":
        <>
            <p><strong>Overlapping</strong> - Course is considered substantially similar to, but not the same as, another course (or courses). Overlapping content courses do not substitute for each other as degree requirements or prerequisites. Credit is only given to one of the courses if a student completes more than one.</p>
            {curricularDisclaimer}
        </>,
    "additionalInfo":
        <>
            <p><strong>Additional Information</strong> - May include information about credit restrictions for this course such as a maximum amount of credits allowed between this course and other courses, a course sequence, or a minimum grade restriction.</p>
            {curricularDisclaimer}
        </>,
};

/**
 * The Course Details page.
 */
class CourseDetails extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        // Instantiate a store.
        this.store = new CourseDetailsStore();
        this.state = {
            showModal: false,
            addPlanFormActive: false,
            hideErrorModal: false
        };

        // Bind "this" to even handler methods.
        this.handleAddToPlanClick = this.handleAddToPlanClick.bind(this);
        this.resetPlanFormActive = this.resetPlanFormActive.bind(this);
        this.showModal = this.showModal.bind(this);
        this.hideModal = this.hideModal.bind(this);
        this.clearErrorModal = this.clearErrorModal.bind(this);
        this.handleLoginClick = this.handleLoginClick.bind(this);
    }

    componentDidMount() {
        // Get query params out of the request.
        const query = parse(this.props.location.search, { ignoreQueryPrefix: true });
        this.store.mount(this.props.params.courseCode, query.id, query.sectionFilters);
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    /**
     * Handle link clicks.
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const previousQuery = parse(prevProps.location.search, {ignoreQueryPrefix: true});
        const query = parse(this.props.location.search, {ignoreQueryPrefix: true});
        // If the course code, course id, or filters change then reinitialize for the new course.
        if (this.props.params.courseCode !== prevProps.params.courseCode
            || query.id !== previousQuery.id
            || query.sectionFilters !== previousQuery.sectionFilters
            || query.section !== previousQuery.section) {

            // Re-initialize the store for the new course version.
            const courseCode = this.props.params.courseCode;
            this.store.mount(courseCode, query.id, query.sectionFilters);
        }
        // Add course summary popover components to course codes in the requisites text.
        LinkBuilder.linkifyDocument(document, "Course-Details-Course-Requisite");
    }

    /**
     * Click handlers.
     */
    handleAddToPlanClick = () => this.setState({showModal: true, addPlanFormActive: true});
    resetPlanFormActive = () => this.setState({showModal: false, addPlanFormActive: false});
    showModal = () => this.setState({showModal: true});
    /**
     * Callback for when the overlay is closed (by clicking away or whatever).
     * This should reset the component.
     */
    hideModal = () => this.setState({showModal: false, addPlanFormActive: false, hideErrorModal: false});

    /**
     * Clear the planItem error message and close the error modal
     */
    clearErrorModal = () => {
        this.store.clearFailedToAddMsg();
        this.setState({hideErrorModal: true});
    }
    /**
     * Handler for the login link for unauth'd users.
     */
    handleLoginClick = () => appStore.doLogin();

    /**
     * Make the page component.
     */
    _makeComponent() {
        const course = this.store.getCourseDetails();
        const authenticated = appStore.isAuthenticated();

        /**
         * Add to Plan and Bookmark look for a property called courseCode instead of code in the course object.
         * code has extra spaces and courseCode is looking for a trimmed version of code.
         */
        course.courseCode = normalize(course.code);

        // Fetch the course section offerings, which is a list of institutions, which contains a list of terms, which contains a list of sections.
        let courseOfferingInstitutions = this.store.getCourseOfferings();

        /**
         * Setup course action buttons (add to plan, bookmark).
         */
        let courseActions = null;
        if (! appStore.isAdviser() ) {
            if (authenticated) {

                let addToPlanButton = <Button variant="primary" onClick={this.handleAddToPlanClick}>Add to Plan</Button>;
                let bookmarkButton = <BookmarkWidget showSuccess={true} code={course.courseCode}
                                                     type={'kuali.lu.type.CreditCourse'} component={'button'} toggle={true}/>;

                let addToPlanOverlay = null;
                if ( this.state.addPlanFormActive ) {
                    addToPlanOverlay =
                        <Modal id={"modal-course-" + course.uiId}
                               show={this.state.showModal}
                               onHide={this.hideModal}
                               onExited={this.resetPlanFormActive}
                               aria-labelledby={"modal-course-" + course.uiId + "-title"}
                               centered>
                            <Modal.Header closeButton>
                                <Modal.Title id={"modal-course-" + course.uiId + "-title"}>
                                    {course.courseCode} &ndash; {course.courseTitle} ({course.credit})
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <AddCourseToPlanForm course={course} onCancel={this.hideModal} analyticsContext="Course-Details"/>
                            </Modal.Body>
                        </Modal>;
                }

                let errorMsg = null;
                if ( this.store.failedToAddMsg && "" !== this.store.failedToAddMsg ) {
                    errorMsg =
                        <Modal id="modal-failure-message"
                               show={this.store.failedToAddMsg && "" !== this.store.failedToAddMsg}
                               onHide={this.clearErrorModal}
                               className="modal-error"
                               aria-labelledby="modal-failure-message-title"
                               centered>
                            <Modal.Header closeButton>
                                <Modal.Title id="modal-failure-message-title">Oops, something went wrong</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p>{this.store.failedToAddMsg}</p>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="small" onClick={this.clearErrorModal}>Close</Button>
                            </Modal.Footer>
                        </Modal>;
                }
                courseActions =
                    <Row>
                        <Col xs={12}>
                            <ButtonToolbar className="align-items-center">
                                {addToPlanButton}
                                {bookmarkButton}
                            </ButtonToolbar>
                            {addToPlanOverlay}
                            {errorMsg}
                        </Col>
                    </Row>;
            } else {
                courseActions =
                    <Row>
                        <Col xs={12} className="mb-3">
                            <Button id="sign-in"
                                    variant="inline-link"
                                    onClick={this.handleLoginClick}>
                                    Sign in to add this course to your plan or bookmark it.
                            </Button>
                        </Col>
                    </Row>;
            }
        }

        // RegEx for replacing self closing html tags with a proper opening and closing tag along with all the original attributes
        const tags = /<(\w*)\s(.*?)['"\d\w\s]\/>/g;

        let courseDescription = "None";
        if (course.hasOwnProperty("courseDescription") && course.courseDescription !== "") {
            /*
             * Data has self closing tags which some how ends up wrapping text that follows it, so replace all
             * self-closing tags with the same element and its own closing tag.
             */
            courseDescription = course.courseDescription.replace(tags, '<$1 $2></$1>');
        }

        let crossListing = null;
        if (course.hasOwnProperty("crossListings") && course.crossListings.length > 0) {
            crossListing =
                <>
                    <dt className="col-12 col-md-3 text-start text-md-end">Offered Jointly With <HelpPopover title="Offered Jointly Help">{CourseDetailsHelp.crossListing}</HelpPopover></dt>
                    <dd className="col-12 col-md-9 mb-3" dangerouslySetInnerHTML={{ __html: course.crossListings.join(", ").replace(tags, '<$1 $2></$1>') }} />
                </>;
        }

        let courseReqs = <dd className="col-12 col-md-9 mb-3">None</dd>;

        let hasCourseReqs = course.hasOwnProperty("requisites") && course.requisites.length;
        if (hasCourseReqs) {
            /*
             * Data has self closing tags which some how ends up wrapping text that follows it, so replace all
             * self-closing tags with the same element and its own closing tag.
             */
            courseReqs =
                <dd className="col-12 col-md-9 mb-3">
                    <p dangerouslySetInnerHTML={{ __html: course.requisites.join(" ").replace(tags, '<$1 $2></$1>') }} />
                </dd>;
        }

        let recommendedPrep = null;
        if (course.recommendedPrep) {
            recommendedPrep =
                <>
                    <dt className="col-12 col-md-3 text-start text-md-end">Recommended Prep <HelpPopover title="Recommended Preparation">{CourseDetailsHelp.recommendedPrep}</HelpPopover></dt>
                    <dd className="col-12 col-md-9 mb-3">{LinkBuilder.linkify(course.recommendedPrep, authenticated)}</dd>
                </>;
        }

        let scheduledTerms = "Not currently offered";
        if (course.hasOwnProperty("scheduledTerms") && course.scheduledTerms.length) {
            let scheduledTermList = course.scheduledTerms.map((term, i) => {
                return (
                    <li className="list-inline-item" key={i}>
                        {TermUtils.makeTermBadge(term, i)}
                    </li>
                );
            });
            scheduledTerms = <ul className="list-inline mb-0">{scheduledTermList}</ul>
        }

        let termsOffered = "Check with the department or your adviser for more information about this course.";
        if (course.hasOwnProperty("termsOffered") && course.termsOffered.length) {
            termsOffered = course.termsOffered.join(", ");
        }

        let genEdReqData = null;
        let genEdReqList = [];
        if (course.hasOwnProperty("genEdRequirements") && course.genEdRequirements.length) {
            genEdReqList = course.genEdRequirements.sort();
        }

        const specialGenEdData =
            <p>
                Conditional A&amp;H: 1<sup>st</sup> year foreign language courses may, under certain conditions, count toward the A&amp;H component of your degree requirement.&nbsp;<a
                href="https://advising.uw.edu/counting-first-year-foreign-language-as-ah/ " target="_blank">See website</a>&nbsp;or your adviser for more information.
            </p>;

        if (genEdReqList.length === 0 && ! course.specialAh) {
            genEdReqData = "None";
        } else if (genEdReqList.length > 0 && !course.specialAh) {
            genEdReqData = <p>{genEdReqList.join(", ")}</p>;
        } else if (genEdReqList.length > 0 && course.specialAh) {
            genEdReqData =
                <>
                    <p>{genEdReqList.join(", ")}</p>
                    {specialGenEdData}
                </>;
        } else if (genEdReqList.length === 0 && course.specialAh) {
            genEdReqData = specialGenEdData;
        }

        let versionsMessaging = null;
        let publishedTerms = termStore.get('publishedTerms');

        if (! isEmpty(course.version) && publishedTerms) {
            versionsMessaging =
                <CourseVersions
                    courseCode={course.courseCode}
                    version={course.version}
                    startTerm={course.startTerm}
                    endTerm={course.endTerm}
                    publishedTerms={publishedTerms} />;
        }

        const docTitle = course.courseCode + " " + course.courseTitle + " - MyPlan";

        let courseMessagesRow = null;
        if (authenticated) {
            courseMessagesRow =
                <Row>
                    <Col xs={12} className="mb-3">
                        <CourseMessages messages={this.store.getCourseMessages()}/>
                    </Col>
                </Row>;
        }

        let dawgPath;
        if ( course.hasOwnProperty("courseNumber") && parseInt(course.courseNumber) < 500 && authenticated ) {
            dawgPath =
                <p>
                    <a href={`https://dawgpath.uw.edu/course?id=${course.courseCode}&campus=${OrgUtils.getCampusNameForCode(course.campusCd)}`} target="_blank">
                        View {hasCourseReqs ? 'prerequisite map and ':''}additional insights for this course on DawgPath.
                    </a>
                </p>
        }

        let equivalentCourses;
        if (course.hasOwnProperty("equivalentCourses") && course.equivalentCourses) {
            equivalentCourses =
                <>
                    <dt className="col-12 col-md-3 text-start text-md-end">Equivalent Courses <HelpPopover title="Equivalent Course Help">{CourseDetailsHelp.equivalentCourses}</HelpPopover></dt>
                    <dd className="col-12 col-md-9 mb-3" dangerouslySetInnerHTML={{ __html: course.equivalentCourses.replace(tags, '<$1 $2></$1>')}}></dd>
                </>
        }

        let overlappingCourses;
        if (course.hasOwnProperty("overlappingCourses") && course.overlappingCourses) {
            overlappingCourses =
                <>
                    <dt className="col-12 col-md-3 text-start text-md-end">Overlapping Courses <HelpPopover title="Overlapping Course Help">{CourseDetailsHelp.overlapping}</HelpPopover></dt>
                    <dd className="col-12 col-md-9 mb-3" dangerouslySetInnerHTML={{ __html: course.overlappingCourses.replace(tags, '<$1 $2></$1>')}}></dd>
                </>
        }

        let additionalInfo;
        if (course.hasOwnProperty("additionalCurricularRelationship") && course.additionalCurricularRelationship) {
            additionalInfo =
                <>
                    <dt className="col-12 col-md-3 text-start text-md-end">Additional Information <HelpPopover title="Additional Information Help">{CourseDetailsHelp.additionalInfo}</HelpPopover></dt>
                    <dd className="col-12 col-md-9 mb-3" dangerouslySetInnerHTML={{ __html: course.additionalCurricularRelationship.replace(tags, '<$1 $2></$1>')}}></dd>
                </>
        }

        return (
            <DocumentTitle title={docTitle}>
                <>
                    <div className="page-heading">
                        <h1>{course.courseCode} {course.courseTitle} ({course.credit})</h1>
                    </div>
                    <div className="cdpCourseDetails">
                        {courseActions}
                        {courseMessagesRow}
                        <Row>
                            <Col xs={12}>
                                <h2>Course Overview</h2>
                                {versionsMessaging}
                                <dl className="row mt-3">
                                    <dt className="col-12 col-md-3 text-start text-md-end">Course Description</dt>
                                    <dd className="col-12 col-md-9 mb-3">
                                        <p dangerouslySetInnerHTML={{ __html: courseDescription }} />
                                        {dawgPath}
                                    </dd>
                                    {crossListing}
                                    <dt className="col-12 col-md-3 text-start text-md-end">Course Prerequisite <HelpPopover title="Course Prerequisite Help">{CourseDetailsHelp.prereq}</HelpPopover></dt>
                                    {courseReqs}
                                    {recommendedPrep}
                                    <dt className="col-12 col-md-3 text-start text-md-end">Quarters <HelpPopover title="Quarters Help">{CourseDetailsHelp.quarter}</HelpPopover></dt>
                                    <dd className="col-12 col-md-9 mb-3">
                                        <div className="d-flex align-items-center"><span className="me-2">Scheduled for:</span> {scheduledTerms}</div>
                                        <div>{termsOffered}</div>
                                    </dd>
                                    <dt className="col-12 col-md-3 text-start text-md-end">Gen Edu Req <HelpPopover title="General Education Requirements Help">{CourseDetailsHelp.gened}</HelpPopover></dt>
                                    <dd className="col-12 col-md-9 mb-3">{genEdReqData}</dd>
                                    <dt className="col-12 col-md-3 text-start text-md-end">Curriculum</dt>
                                    <dd className="col-12 col-md-9 mb-3">{course.curriculumTitle}, <span className="text-capitalize">{OrgUtils.getCampusNameForCode(course.campusCd)} campus</span></dd>
                                    {equivalentCourses}
                                    {overlappingCourses}
                                    {additionalInfo}
                                </dl>
                            </Col>
                        </Row>
                        <SectionsPanel
                            course={course}
                            institutions={courseOfferingInstitutions}
                            filtersApplied={this.store.getFiltersApplied()}
                            {...this.props} />
                    </div>
                </>
            </DocumentTitle>
        );
    }

    render() {
        let component;
        // IMPORTANT: Dereference the observable so that the render method gets called.
        // https://github.com/mobxjs/mobx/blob/gh-pages/docs/best/react.md#mobx-only-tracks-data-accessed-for-observer-components-if-they-are-directly-accessed-by-render
        // Putting the reference in the switch below doesn't work.

        const state = this.store.state;

        // The data is available then render the widget.
        if (this.store.hasCourseData()) {
           component = this._makeComponent();
        } else {
            // If there isn't any course data ...
            switch (state) {
                case States.initial:
                case States.pending:
                    component = <Loading type="dots" alt="Loading Course Details ..." />;
                    break;
                case States.done:
                case States.error:
                    component = <Broken name="CourseDetails"/>;
                    break;
                default:
                    component = <UnknownState name="CourseDetails"/>;
            }
        }
        return component;
    }
}

export default withRouter(observer(CourseDetails));